import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  NotificationCenterItem,
  NotificationCenterList,
} from '@/components/ui/notification-center';
import { useEntitlements } from '@/hooks/useEntitlements';
import { AllIntegrations } from '@/hooks/useIntegration';
import { useIntegrationDisplay } from '@/hooks/useIntegrationDisplay';
import { useIntegrationSuggestions } from '@/hooks/useIntegrationSuggestions';
import { useGlobalState } from '@/state/global.state';
import { Integration } from '@/types';

interface SuggestionsInboxProps {
  /** Closes the Activity Hub popover (Connect hands off to the drawer). */
  onClose: () => void;
}

/**
 * "Suggestions" section of the Activity Hub popover: one row per undismissed
 * integration suggestion from `useIntegrationSuggestions`.
 *
 * Connect follows the IntegrationSuggestionBanner precedent — resolve the meta
 * object via `AllIntegrations[name as Integration]` (only `.name` reads are
 * ESLint-banned; display names go through `useIntegrationDisplay`), open the
 * global integration drawer on the 'All' category, and close the popover.
 * Dismiss persists server-side via the hook. Names render strictly as JSX text
 * nodes (MUST-3 — no HTML injection path).
 */
export function SuggestionsInbox({ onClose }: SuggestionsInboxProps) {
  const { suggestions, dismiss, isDismissing } = useIntegrationSuggestions();
  const { getDisplayName } = useIntegrationDisplay();
  const { drawerState } = useGlobalState();
  // Dismiss persists via PUT /setting, which the backend gates on
  // EntitlementManageSettings — read-only users must not see a button whose
  // request can only 403.
  const { canManageSettings } = useEntitlements();

  if (suggestions.length === 0) {
    return null;
  }

  function handleConnect(name: string) {
    const meta = AllIntegrations[name as Integration];
    if (!meta) {
      return;
    }
    drawerState.handleOpenIntegrationDrawer(meta, 'All');
    onClose();
  }

  return (
    <div>
      <div className="border-chariot-stroke flex items-center justify-between border-y px-4 py-2">
        <span className="text-chariot-text-primary text-sm font-semibold">
          Suggestions
        </span>
        <span className="text-chariot-text-secondary text-xs">
          {suggestions.length} suggested
        </span>
      </div>
      <NotificationCenterList>
        {suggestions.map(name => (
          <NotificationCenterItem key={name}>
            <div className="min-w-0 flex-1">
              <p className="text-chariot-text-primary truncate text-sm font-medium">
                {getDisplayName(name)}
              </p>
              <p className="text-chariot-text-secondary text-xs">
                Suggested integration
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleConnect(name)}
              >
                Connect
              </Button>
              {canManageSettings && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isDismissing}
                  onClick={() => {
                    dismiss(name).catch(() =>
                      toast.error('Failed to dismiss suggestion')
                    );
                  }}
                >
                  Dismiss
                </Button>
              )}
            </div>
          </NotificationCenterItem>
        ))}
      </NotificationCenterList>
    </div>
  );
}
